import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiInfo, FiCheck, FiAlertCircle, FiDownload } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { setFormDirty, registerAutosaveHandler } from '../../../lib/unsavedChanges';
import {
  getTaxCodeLists, getTaxCodeList, saveTaxLineEdit, getCoaTaxLines,
  TaxCodeList, TaxCodeLine, TaxCodeVersion,
} from '../coaTemplateApi';
import { taxCodeTopGroup } from './TaxCodePicker';
import './GLChartOfAccountsTab.css';

// =============================================
// GL Chart of Accounts Setup → Tax Codes (migration 148)
// The tax-return code list of each country the account files in (Canada: GIFI), as
// published — by VERSION (a list changes every few years; a year uses the version in
// force on its year end) — next to the account's own edits: its wording, a note, and
// "hide from the pickers". The published code and name never change. Also shows which
// accounts use each code. Edits save as each box is left (no Save button), and apply
// to the code in every version.
// =============================================

interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

type Edit = { own_name: string; own_note: string; is_hidden: boolean };
const sameEdit = (a: Edit, b: Edit) => a.own_name.trim() === b.own_name.trim() && a.own_note.trim() === b.own_note.trim() && a.is_hidden === b.is_hidden;
const editOf = (l: TaxCodeLine): Edit => ({ own_name: l.own_name || '', own_note: l.own_note || '', is_hidden: !!Number(l.is_hidden) });
const fmtDate = (d: string | null) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const TaxCodesTab: React.FC<Props> = () => {
  const [lists, setLists] = useState<TaxCodeList[] | null>(null);
  const [error, setError] = useState('');
  const [formId, setFormId] = useState<number | null>(null);
  const [versionCode, setVersionCode] = useState('');
  const [data, setData] = useState<{ version: TaxCodeVersion | null; previous: string | null; lines: TaxCodeLine[]; removed: { line_code: string; line_name: string }[] } | null>(null);
  const [loading, setLoading] = useState(false);
  // edits typed (code -> edit) and what is saved on the server
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const saved = useRef<Record<string, Edit>>({});
  const [savedAt, setSavedAt] = useState<Record<string, number>>({});
  const [saveErr, setSaveErr] = useState<Record<string, string>>({});
  // filters
  const [search, setSearch] = useState('');
  const [statement, setStatement] = useState<'' | 'BS' | 'IS'>('');
  const [onlyEdited, setOnlyEdited] = useState(false);
  const [onlyUsed, setOnlyUsed] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(false);
  // accounts using each code: number + name as in the GL Chart of Accounts
  const [usage, setUsage] = useState<Map<string, { number: string; name: string }[]>>(new Map());

  useEffect(() => {
    let stale = false;
    getTaxCodeLists().then(l => {
      if (stale) return;
      setLists(l);
      if (l.length) { setFormId(l[0].tax_form_id); setVersionCode(l[0].current_version_code || ''); }
    }).catch(e => { if (!stale) { setError(e?.message || 'Could not load the code lists.'); setLists([]); } });
    return () => { stale = true; };
  }, []);

  const list = useMemo(() => lists?.find(l => l.tax_form_id === formId) || null, [lists, formId]);

  useEffect(() => {
    if (!formId) return;
    let stale = false;
    setLoading(true);
    getTaxCodeList(formId, versionCode || undefined).then(d => {
      if (stale) return;
      setData(d);
      const s: Record<string, Edit> = {};
      d.lines.forEach(l => { s[l.line_code] = editOf(l); });
      saved.current = s;
      setEdits(s);
    }).catch(e => { if (!stale) setError(e?.message || 'Could not load the codes.'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [formId, versionCode]);

  // which accounts use each code (all sets of books of this account)
  useEffect(() => {
    if (!list) return;
    let stale = false;
    Promise.all([getCoaTaxLines(), loadTable('acctg_coa').catch(() => getTableData('acctg_coa'))]).then(([tl, coa]) => {
      if (stale) return;
      const byId = new Map((coa as any[]).filter(a => Number(a.is_deleted) !== 1).map(a => [Number(a.coa_id), a]));
      const m = new Map<string, { number: string; name: string }[]>();
      tl.filter(r => r.tax_form_code === list.tax_form_code).forEach(r => {
        const a = byId.get(Number(r.coa_id));
        if (!a) return;
        m.set(r.line_code, [...(m.get(r.line_code) || []), { number: String(a.account_number || ''), name: String(a.account_name || '') }]);
      });
      m.forEach(v => v.sort((x, y) => x.number.localeCompare(y.number)));
      setUsage(m);
    }).catch(() => { /* usage is extra */ });
    return () => { stale = true; };
  }, [list]);

  // ---- saving one code's edit (on leaving the box / toggling Hide) ----
  const saveCode = useCallback(async (code: string, next?: Edit) => {
    if (!formId) return;
    const e = next || edits[code];
    if (!e || sameEdit(e, saved.current[code] || { own_name: '', own_note: '', is_hidden: false })) return;
    try {
      await saveTaxLineEdit({ tax_form_id: formId, line_code: code, own_name: e.own_name, own_note: e.own_note, is_hidden: e.is_hidden });
      saved.current = { ...saved.current, [code]: { ...e } };
      setSavedAt(p => ({ ...p, [code]: Date.now() }));
      setSaveErr(p => { const n = { ...p }; delete n[code]; return n; });
    } catch (err: any) {
      setSaveErr(p => ({ ...p, [code]: err?.message || 'Not saved' }));
    }
    const pending = Object.keys(edits).some(k => k !== code && !sameEdit(edits[k], saved.current[k] || editOf({} as any)));
    if (!pending) setFormDirty(false);
  }, [formId, edits]);
  const flushAll = useCallback(async () => {
    for (const code of Object.keys(edits)) {
      if (!sameEdit(edits[code], saved.current[code] || { own_name: '', own_note: '', is_hidden: false })) await saveCode(code);
    }
    setFormDirty(false);
  }, [edits, saveCode]);
  // leaving the tab saves what is typed (no "unsaved changes" prompt)
  useEffect(() => {
    registerAutosaveHandler(() => flushAll());
    return () => registerAutosaveHandler(null);
  }, [flushAll]);
  useEffect(() => () => setFormDirty(false), []);

  const change = (code: string, patch: Partial<Edit>) => {
    setFormDirty(true);
    setEdits(p => ({ ...p, [code]: { ...(p[code] || { own_name: '', own_note: '', is_hidden: false }), ...patch } }));
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.lines.filter(l => {
      const e = edits[l.line_code];
      if (statement && l.statement !== statement) return false;
      if (onlyEdited && !(e && (e.own_name || e.own_note || e.is_hidden))) return false;
      if (onlyUsed && !usage.has(l.line_code)) return false;
      if (onlyChanged && !l.changed) return false;
      if (!q) return true;
      const accts = (usage.get(l.line_code) || []).map(a => `${a.number} ${a.name}`).join(' ');
      return [l.line_code, l.line_name, l.line_group, taxCodeTopGroup(l.line_code), l.includes_text, e?.own_name, e?.own_note, accts]
        .some(v => String(v || '').toLowerCase().includes(q));
    });
  }, [data, search, statement, onlyEdited, onlyUsed, onlyChanged, edits, usage]);

  // CSV of what is shown, in the owner's sheet layout: one row per account on a code
  // (a code no account uses gets one row with the account columns blank)
  const downloadCsv = () => {
    if (!list || !data) return;
    const q = (v: unknown) => { const s = String(v == null ? '' : v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = [list.tax_form_code, 'Code', 'Account name as per GL COA', `Account name as per ${list.tax_form_code}`, 'Your name for the code',
      'Group', 'Sub-group', 'Financial statements', `Description as per ${list.tax_form_code}`, 'Your note', 'Hidden'];
    const out = [head.map(q).join(',')];
    rows.forEach(l => {
      const e = edits[l.line_code] || { own_name: '', own_note: '', is_hidden: false };
      const base = (num: string, name: string) => [l.line_code, num, name, l.line_name, e.own_name, taxCodeTopGroup(l.line_code),
        l.line_group || '', l.statement === 'BS' ? 'Balance Sheet' : 'Income Statement',
        l.includes_text ? `${l.line_name} - ${l.includes_text}` : l.line_name, e.own_note, e.is_hidden ? 'yes' : ''];
      const used = usage.get(l.line_code) || [];
      if (used.length) used.forEach(a => out.push(base(a.number, a.name).map(q).join(',')));
      else out.push(base('', '').map(q).join(','));
    });
    const blob = new Blob([out.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${list.tax_form_code}_${data.version?.version_code || ''}_codes.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const editedCount = useMemo(() => Object.values(edits).filter(e => e.own_name || e.own_note || e.is_hidden).length, [edits]);

  if (lists === null) return <div className="coa-tab coa-tab-wide"><div className="coa-empty"><p>Loading…</p></div></div>;
  if (!lists.length) {
    return (
      <div className="coa-tab coa-tab-wide">
        {error && <div className="coa-field-error" style={{ marginBottom: 12 }}><FiAlertCircle size={14} /> {error}</div>}
        <div className="coa-empty">
          <div className="coa-empty-icon"><FiInfo size={40} /></div>
          <p>There is no tax-return code list for the countries of your businesses yet.
            (Code lists so far: Canada — GIFI.) Add the business address in Family &amp; Businesses.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="coa-tab coa-tab-wide">
      <div className="coa-info-banner">
        <FiInfo size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
        The {list?.tax_form_code} codes as published by the tax authority ({list?.country_name}), next to your own edits.
        Your wording, note and "hide" are yours only — the published code and name never change — and apply to the code
        in every version. A year uses the version in force on its year end.
      </div>
      {error && <div className="coa-field-error" style={{ marginBottom: 12 }}><FiAlertCircle size={14} /> {error}</div>}

      <div className="coa-taxcodes-controls">
        {lists.length > 1 && (
          <label className="coa-field-group">
            <span className="coa-field-label">Code list</span>
            <select className="coa-acct-input" value={formId || ''} onChange={e => {
              const f = lists.find(l => l.tax_form_id === Number(e.target.value));
              setFormId(Number(e.target.value)); setVersionCode(f?.current_version_code || '');
            }}>
              {lists.map(l => <option key={l.tax_form_id} value={l.tax_form_id}>{l.country_name} — {l.tax_form_code}</option>)}
            </select>
          </label>
        )}
        <label className="coa-field-group">
          <span className="coa-field-label">Version</span>
          <select className="coa-acct-input" value={versionCode} onChange={e => setVersionCode(e.target.value)}>
            {list?.versions.map(v => (
              <option key={v.version_code} value={v.version_code}>
                {v.version_label}{v.version_code === list.current_version_code ? ' — current' : ''} ({v.codes} codes)
              </option>
            ))}
          </select>
        </label>
        <label className="coa-field-group" style={{ flex: 1, minWidth: 200 }}>
          <span className="coa-field-label">Search</span>
          <input className="coa-acct-input" type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Code, name, your wording, or a word the guide uses (e.g. hydro)" />
        </label>
        <label className="coa-field-group">
          <span className="coa-field-label">Statement</span>
          <select className="coa-acct-input" value={statement} onChange={e => setStatement(e.target.value as any)}>
            <option value="">Both</option>
            <option value="BS">Balance sheet</option>
            <option value="IS">Income statement</option>
          </select>
        </label>
      </div>
      <div className="coa-taxcodes-filters">
        <label className="coa-acct-check"><input type="checkbox" checked={onlyEdited} onChange={e => setOnlyEdited(e.target.checked)} /> Only my edits ({editedCount})</label>
        <label className="coa-acct-check"><input type="checkbox" checked={onlyUsed} onChange={e => setOnlyUsed(e.target.checked)} /> Only codes my accounts use ({usage.size})</label>
        {data?.previous && (
          <label className="coa-acct-check"><input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} /> Only new / renamed since {data.previous}</label>
        )}
        <button type="button" className="coa-btn-secondary" style={{ marginLeft: 'auto', fontSize: 13, padding: '4px 10px' }}
          onClick={downloadCsv} title="The codes shown, one row per account on a code">
          <FiDownload size={13} /> Download CSV
        </button>
      </div>

      {data?.version && (
        <p className="coa-field-hint" style={{ margin: '0 2px 10px' }}>
          {data.version.version_label}: year ends from {fmtDate(data.version.effective_from)}
          {data.version.effective_to ? ` to ${fmtDate(data.version.effective_to)}` : ' on'}. {data.version.notes || ''}
          {data.version.source_ref ? ` Source: ${data.version.source_ref}.` : ''}
        </p>
      )}

      {loading ? <div className="coa-empty"><p>Loading codes…</p></div> : (
        <div className="coa-taxcodes-wrap">
          <table className="coa-taxcodes-table">
            <thead>
              <tr>
                <th style={{ width: 62 }}>{list?.tax_form_code}</th>
                <th style={{ width: '17%' }}>Account name as per {list?.tax_form_code}</th>
                <th style={{ width: '22%' }}>Description as per {list?.tax_form_code}</th>
                <th style={{ width: '10%' }}>Group</th>
                <th style={{ width: 90 }}>Financial statements</th>
                <th style={{ width: '15%' }}>Your name</th>
                <th style={{ width: '12%' }}>Your note</th>
                <th style={{ width: 44 }}>Hide</th>
                <th style={{ width: '14%' }}>Accounts (code · name as per GL COA)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(l => {
                const e = edits[l.line_code] || { own_name: '', own_note: '', is_hidden: false };
                const used = usage.get(l.line_code) || [];
                return (
                  <tr key={l.line_code} className={`${Number(l.is_heading) ? 'is-heading' : ''} ${e.is_hidden ? 'is-hidden' : ''}`}>
                    <td className="coa-taxcodes-code">{l.line_code}</td>
                    <td>
                      {l.line_name}
                      {l.changed === 'added' && <span className="coa-dropdown-badge" style={{ marginLeft: 6 }}>new in {data?.version?.version_code}</span>}
                      {l.changed === 'renamed' && <span className="coa-dropdown-badge" style={{ marginLeft: 6 }}>renamed</span>}
                    </td>
                    <td className="coa-taxcodes-desc">{l.includes_text || <span className="coa-field-hint">—</span>}</td>
                    <td>
                      {taxCodeTopGroup(l.line_code)}
                      {l.line_group && <div className="coa-taxcodes-group">{l.line_group}</div>}
                    </td>
                    <td>{l.statement === 'BS' ? 'Balance Sheet' : 'Income Statement'}</td>
                    <td>
                      <input className="coa-acct-input" type="text" value={e.own_name} maxLength={255} placeholder="—"
                        aria-label={`Your name for ${l.line_code}`}
                        onChange={ev => change(l.line_code, { own_name: ev.target.value })}
                        onBlur={() => saveCode(l.line_code)}
                        onKeyDown={ev => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }} />
                    </td>
                    <td>
                      <input className="coa-acct-input" type="text" value={e.own_note} maxLength={2000} placeholder="—"
                        aria-label={`Your note for ${l.line_code}`}
                        onChange={ev => change(l.line_code, { own_note: ev.target.value })}
                        onBlur={() => saveCode(l.line_code)}
                        onKeyDown={ev => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={e.is_hidden} aria-label={`Hide ${l.line_code} from the pickers`}
                        title="Hide from the code pickers (an account already on it keeps it)"
                        onChange={ev => { const n = { ...e, is_hidden: ev.target.checked }; change(l.line_code, { is_hidden: ev.target.checked }); saveCode(l.line_code, n); }} />
                    </td>
                    <td className="coa-taxcodes-used">
                      {used.length ? used.map(a => (
                        <div key={`${a.number}-${a.name}`}><span className="coa-taxcodes-code">{a.number}</span> {a.name}</div>
                      )) : <span className="coa-field-hint">—</span>}
                      {savedAt[l.line_code] && !saveErr[l.line_code] && <span className="coa-taxcodes-saved"><FiCheck size={12} /> saved</span>}
                      {saveErr[l.line_code] && <span className="coa-field-error">{saveErr[l.line_code]}</span>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="coa-field-hint" style={{ padding: 16 }}>No code matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && data.removed.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="coa-field-label">Removed in {data.version?.version_code} (were in {data.previous})</div>
          <ul className="coa-field-hint" style={{ margin: '4px 0 0 18px' }}>
            {data.removed.map(r => (
              <li key={r.line_code}>{r.line_code} {r.line_name}{usage.has(r.line_code) ? ` — still on: ${usage.get(r.line_code)!.map(a => `${a.number} ${a.name}`).join(', ')}` : ''}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TaxCodesTab;
